import {
  type PropsWithChildren,
  type Dispatch,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import { type FilterValue } from "@shopify/hydrogen-react/storefront-api-types";

import { type DropdownOption } from "@components/Dropdown";
import { COLLECTION_FILTER_IDS } from "@react/sections/Collection/CollectionFilters/constants";
import { generatePriceFilterValue } from "@react/utilities/collection";
import { type PriceRange, type SortValue, SortKeys } from "@tablescape/state";

export type CollectionFiltersContextValues = {
  addFilter: (filter: FilterValue) => void;
  appliedFilters: FilterValue[];
  clearFilters: () => void;
  collectionPriceMax: number;
  filterPriceRange: PriceRange;
  hasCompletedFirstRender: boolean;
  markFirstRenderComplete: () => void;
  onPriceRangeChange: (range: PriceRange) => void;
  removeFilter: (filter: FilterValue) => void;
  removePriceFilter: () => void;
  setCollectionPriceMax: Dispatch<SetStateAction<number>>;
  sortValue: SortValue;
  updateSortValue: (option: DropdownOption) => void;
};

const CollectionFiltersContext = createContext<
  CollectionFiltersContextValues | undefined
>(undefined);

type Props = {
  value?: CollectionFiltersContextValues;
};

function CollectionFiltersProvider({
  children,
  value: providedValue,
}: PropsWithChildren<Props>) {
  const hasCompletedFirstRender = useRef(false);

  const [appliedFilters, setAppliedFilters] = useState<FilterValue[]>([]);
  const [sortValue, setSortValue] = useState<SortValue>({
    sortKey: SortKeys.COLLECTION_DEFAULT,
    reverse: false,
  });
  const [collectionPriceMax, setCollectionPriceMax] = useState<number>(0);
  const [filterPriceRange, setFilterPriceRange] = useState<PriceRange>({
    min: 0,
    max: undefined,
  });

  const markFirstRenderComplete = useCallback(() => {
    hasCompletedFirstRender.current = true;
  }, []);

  const addFilter = useCallback((filter: FilterValue) => {
    setAppliedFilters((prev) => [...prev, filter]);
  }, []);

  const removeFilter = useCallback((filter: FilterValue) => {
    setAppliedFilters((prev) => prev.filter((f) => f.id !== filter.id));
  }, []);

  const resetFilterPriceRange = useCallback(() => {
    setFilterPriceRange({ min: 0, max: collectionPriceMax });
  }, [collectionPriceMax]);

  const removePriceFilter = useCallback(() => {
    setAppliedFilters((prev) =>
      prev.filter((f) => f.id !== COLLECTION_FILTER_IDS.PRICE)
    );
    resetFilterPriceRange();
  }, [resetFilterPriceRange]);

  const clearFilters = useCallback(() => {
    setAppliedFilters([]);
    resetFilterPriceRange();
  }, [resetFilterPriceRange]);

  const updateSortValue = useCallback(
    (option: DropdownOption) => {
      setSortValue(option.value as SortValue);
    },
    [setSortValue]
  );

  const onPriceRangeChange = useCallback(
    (range: PriceRange) => {
      removePriceFilter();
      setFilterPriceRange(range);
      addFilter(generatePriceFilterValue(range.min, range.max ?? 0));
    },
    [removePriceFilter, addFilter]
  );

  const value = useMemo(
    () => ({
      ...providedValue,
      addFilter,
      appliedFilters,
      clearFilters,
      collectionPriceMax,
      filterPriceRange,
      hasCompletedFirstRender: hasCompletedFirstRender.current,
      markFirstRenderComplete,
      onPriceRangeChange,
      removeFilter,
      removePriceFilter,
      setCollectionPriceMax,
      sortValue,
      updateSortValue,
    }),
    [
      providedValue,
      appliedFilters,
      addFilter,
      removeFilter,
      clearFilters,
      removePriceFilter,
      sortValue,
      updateSortValue,
      filterPriceRange,
      collectionPriceMax,
      onPriceRangeChange,
      markFirstRenderComplete,
    ]
  );

  return (
    <CollectionFiltersContext.Provider value={value}>
      {children}
    </CollectionFiltersContext.Provider>
  );
}

function useCollectionFilters() {
  const context = useContext<CollectionFiltersContextValues | undefined>(
    CollectionFiltersContext
  );

  if (context === undefined) {
    throw new Error(
      "useCollectionFilters must be used within a CollectionFiltersProvider"
    );
  }

  return context;
}

export {
  CollectionFiltersContext,
  CollectionFiltersProvider,
  useCollectionFilters,
};
